import { AnimationBaseController, IAnimationControllerConfiguration, Inject } from '@denishussar/html5-game-engine';
import { GameLogic } from '../../Logic/GameLogic';
import { UnitModel } from '../../Models/UnitModel';
import { SpineAnimationBaseController } from '../Spine/SpineAnimationBaseController';
import { AnimationContents } from '../../../AnimationContents';

export interface IUnitControllerOptions {
    skin: string;
    wear: string;
    gender: string;
    hat: string;
}

export class UnitController extends AnimationBaseController {
    $model: UnitModel;

    private _spineAnimationController: SpineAnimationBaseController;
    private _scale: number = 0.5;

    public constructor(
        @Inject private _options: IUnitControllerOptions,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('UnitController');
    }

    private getAnimationName(name: string): string {
        return `${name}_char_${this._options.skin}_${this._options.hat}`;
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.GAME_SCREEN_JSON,
            libraryName: 'unit_container',
            childControllers: {
                manAnimationController: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        key: `character`,
                        url: `/images/spine/skin_${this._options.wear}_${this._options.gender}/man_1.json`,
                        animations: [
                            this.getAnimationName('idle'),
                            this.getAnimationName('run'),
                            this.getAnimationName('win'),
                        ],
                    },
                },
            },
            modelWatch: {
                move: (move: [number, number]) => {
                    this._spineAnimationController.getSpine().state.onComplete = null;
                    this.$model.playWin = false;

                    if (Math.abs(move[0]) > 0 || Math.abs(move[1]) > 0) {
                        this._spineAnimationController.setAnimation(this.getAnimationName('run'), true);
                    } else {
                        this._spineAnimationController.setAnimation(this.getAnimationName('idle'), true);
                    }

                    if (move[0] < 0) {
                        this._spineAnimationController.scaleX = this._scale;
                    } else if (move[0] > 0) {
                        this._spineAnimationController.scaleX = -this._scale;
                    }
                },
                pos: (pos: [number, number]) => {
                    this._spineAnimationController.x = pos[0];
                    this._spineAnimationController.y = pos[1];

                    this.updateScreenPosition(pos);
                },
                visible: (visible: boolean) => {
                    this._spineAnimationController.visible = visible;
                },
                playWin: (value: boolean) => {
                    if (value) {
                        this._spineAnimationController.setAnimation(this.getAnimationName('win'), false);
                        this._spineAnimationController.getSpine().state.onComplete = () => {
                            this._spineAnimationController.getSpine().state.onComplete = null;
                            this._spineAnimationController.setAnimation(this.getAnimationName('idle'), false);

                            this.$model.playWin = false;
                        }
                    }
                }
            },
            queue: {},
        };
    }

    protected onInitialize(): void {
        this.getDisplayObjectByQuery('dummy')[0].visible = false;

        this._spineAnimationController = this.$childControllers.get('manAnimationController');

        this._spineAnimationController.x = this.$model.pos[0];
        this._spineAnimationController.y = this.$model.pos[1];
        this._spineAnimationController.scaleX = this._scale;
        this._spineAnimationController.scaleY = this._scale;

        this._spineAnimationController.visible = this.$model.visible;
        this._spineAnimationController.display.hitArea = new PIXI.Rectangle(-50, -200, 100, 250);

        this._spineAnimationController.setAnimation(this.getAnimationName('idle'), true);

        this.updateScreenPosition(this.$model.pos);
    }

    private updateScreenPosition(pos: [number, number]): void {
        const global = this._spineAnimationController.display.parent.toGlobal(
            new PIXI.Point(pos[0], pos[1]),
        );

        if (
            global.x !== this.$model.screenPos[0] ||
            global.y !== this.$model.screenPos[1]
        ) {
            this.$model.screenPos = [global.x, global.y];
        }
    }
}
